// Live quotes and chart bars update much more often than the rest of chat.
// Keep them in a small external store so a quote for one chart key does not
// invalidate every ChatContext consumer or every historical ChatMessage.
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from "react";

const LiveQuoteContext = createContext(null);

const initialLiveQuoteState = () => ({
  latestTickerPrices: {},
  displayUpdatedInfo: null,
});

const resolveUpdate = (update, previous) =>
  typeof update === "function" ? update(previous) : update;

export const selectLiveTickerPrices = (snapshot, chartKey) => {
  if (!chartKey) return null;
  return snapshot?.latestTickerPrices?.[chartKey] || null;
};

export const selectLiveDisplayInfo = (snapshot, tickerId) => {
  if (tickerId == null || tickerId === "") return null;
  return snapshot?.displayUpdatedInfo?.[String(tickerId)] || null;
};

export const createLiveQuoteStore = (initialState = {}) => {
  let state = {
    ...initialLiveQuoteState(),
    ...(initialState || {}),
  };
  const listeners = new Set();

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const updateSlice = (key, update) => {
    const previous = state[key];
    const next = resolveUpdate(update, previous);
    if (Object.is(previous, next)) return previous;
    state = {
      ...state,
      [key]: next,
    };
    notify();
    return next;
  };

  return {
    getSnapshot: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    setLatestTickerPrices(update) {
      return updateSlice("latestTickerPrices", update);
    },
    setDisplayUpdatedInfo(update) {
      return updateSlice("displayUpdatedInfo", update);
    },
    resetLiveQuotes() {
      state = initialLiveQuoteState();
      notify();
    },
  };
};

export const LiveQuoteProvider = ({ children }) => {
  const storeRef = useRef(null);
  if (!storeRef.current) {
    storeRef.current = createLiveQuoteStore();
  }

  return (
    <LiveQuoteContext.Provider value={storeRef.current}>
      {children}
    </LiveQuoteContext.Provider>
  );
};

const useLiveQuoteStore = () => {
  const store = useContext(LiveQuoteContext);
  if (!store) {
    throw new Error(
      "useLiveQuoteContext must be used inside a <LiveQuoteProvider>",
    );
  }
  return store;
};

export const useLiveQuoteActions = () => {
  const store = useLiveQuoteStore();
  return {
    getLiveQuoteSnapshot: store.getSnapshot,
    setLatestTickerPrices: store.setLatestTickerPrices,
    setDisplayUpdatedInfo: store.setDisplayUpdatedInfo,
    resetLiveQuotes: store.resetLiveQuotes,
  };
};

export const useLiveTickerPrices = (chartKey) => {
  const store = useLiveQuoteStore();
  const getSnapshot = useCallback(() => {
    return selectLiveTickerPrices(store.getSnapshot(), chartKey);
  }, [chartKey, store]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
};

export const useLiveDisplayInfo = (tickerId) => {
  const store = useLiveQuoteStore();
  const normalizedTickerId =
    tickerId == null || tickerId === "" ? null : String(tickerId);
  const getSnapshot = useCallback(() => {
    return selectLiveDisplayInfo(store.getSnapshot(), normalizedTickerId);
  }, [normalizedTickerId, store]);

  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
};

export const useLiveQuoteContext = () => {
  const store = useLiveQuoteStore();
  const snapshot = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );

  return {
    ...snapshot,
    setLatestTickerPrices: store.setLatestTickerPrices,
    setDisplayUpdatedInfo: store.setDisplayUpdatedInfo,
    resetLiveQuotes: store.resetLiveQuotes,
  };
};

export default LiveQuoteContext;
